import { AnchorEnum, CanvasElement, ResetAnimationEnum, SizedCanvasElement } from '../canvas-element.js';
import { Rect, Point, Segment } from '../../graphics/geometry.js';
import { AnimationHelper } from '../animation/animation-handler.js';
import { simplify } from '../../utils/simplify.js';

class ShapeElement extends SizedCanvasElement {
    constructor(style, hitable, name) {
        super(hitable, name);
        this.style = style;
    }
    getRect(round = false) {
        var _a, _b, _c;
        const r = super.getRect(round);
        if (round) {
            const lineAddition = ((_b = this.style.getLineWidth((_a = this.board) === null || _a === void 0 ? void 0 : _a.transform)) !== null && _b !== void 0 ? _b : 1 + ((_c = this.blur) !== null && _c !== void 0 ? _c : 0)) * 2 + 2;
            if (lineAddition > 0) {
                r.grow(Math.ceil(lineAddition));
            }
        }
        return r;
    }
    drawLineShadow(ctx) {
        const oldStyle = ctx.strokeStyle;
        const oldWidth = ctx.lineWidth;
        try {
            const color = this.style.lineShadowColor;
            if (!color) {
                return;
            }
            if (this.style.lineShadowUseAlpha) {
                const alphaMult = 1 / (this.style.lineShadowSize + 1);
                for (let i = 0; i < this.style.lineShadowSize; i++) {
                    ctx.lineWidth = oldWidth + this.applyScale(i + 2, "round");
                    color.alpha = alphaMult; // 1 - alphaMult * (i + 1);
                    ctx.strokeStyle = color.toRgbaString();
                    ctx.stroke();
                }
            }
            else {
                ctx.lineWidth = this.applyScale(this.style.lineShadowSize * 2 + 1, "");
                color.alpha = 0.5;
                ctx.strokeStyle = color.toRgbaString();
                ctx.stroke();
            }
        }
        finally {
            ctx.strokeStyle = oldStyle;
            ctx.lineWidth = oldWidth;
        }
    }
    drawShape(ctx, prepare, fill, style) {
        style = style || this.style;
        if (prepare) {
            style.prepareCanvasContext(ctx, this);
        }
        if (style.lineShadowSize && style.lineShadowColor) {
            this.drawLineShadow(ctx);
            style.prepareCanvasContext(ctx, this);
        }
        if (fill && style.fillStyle) {
            ctx.fill();
        }
        if (style.strokeStyle) {
            ctx.stroke();
        }
    }
    /**
     * Update the shape style with the new values
     * @param newValues new values to apply
     */
    updateStyle(newValues) {
        var _a;
        if (!newValues || typeof newValues != "object") {
            return;
        }
        this.beforeUpdate();
        if ((_a = AnimationHelper.copyMatchingKeyValues(newValues, this.style)) === null || _a === void 0 ? void 0 : _a.length) {
            this.afterUpdate();
        }
    }
}
class CircleElement extends ShapeElement {
    constructor(x, y, _radius, style, hitable, name) {
        super(style, hitable, name);
        this._radius = _radius;
        this._x = x;
        this._y = y;
        this.anchor = AnchorEnum.Middle;
        this.setSize();
    }
    afterUpdate() {
        this.setSize();
        super.afterUpdate();
    }
    get radius() {
        return this._radius;
    }
    set radius(value) {
        if (this._radius != value) {
            this.beforeUpdate();
            this._radius = value;
            this.afterUpdate();
        }
    }
    setSize() {
        var _a, _b, _c;
        const lineAddition = (_b = this.style.getLineWidth((_a = this.board) === null || _a === void 0 ? void 0 : _a.transform)) !== null && _b !== void 0 ? _b : (1 + ((_c = this.blur) !== null && _c !== void 0 ? _c : 0));
        this._width = Math.ceil((this._radius + lineAddition) * 2);
        this._height = this._width;
    }
    doDraw(ctx /*, clipRect?: Rect*/) {
        if (!this.style.fillStyle && !this.style.strokeStyle) {
            return false;
        }
        ctx.beginPath();
        ctx.ellipse(this.applyTransformX(this._x, true), this.applyTransformY(this._y, true), this.applyScale(this.radius, ""), this.applyScale(this.radius, ""), 0, 0, Math.PI * 2);
        ctx.closePath();
        this.drawShape(ctx, true, true);
        return true;
    }
    hit(p, additionalError) {
        if (!this.opacity) {
            return null;
        }
        const position = this.getPosition();
        const distance = p.distance(position);
        const res = this.hitTarget && distance <= this.radius + this.hitExpansion + additionalError ? this : null;
        if (CanvasElement._DEBUG_) {
            console.debug(`Circle hit check ${this.name} pos:${position.toString(1)} vs hit:${p.toString(1)}; ${distance} <= ${this.radius} + ${this.hitExpansion} + ${additionalError}: ${res}`);
        }
        return res;
    }
}
class RectElement extends ShapeElement {
    constructor(x, y, width, height, style, hitable, name) {
        super(style, hitable, name);
        this._x = x;
        this._y = y;
        this._width = width;
        this._height = height;
        this.anchor = AnchorEnum.Middle;
    }
    doDraw(ctx /*, clipRect?: Rect*/) {
        if (!this.style.fillStyle && !this.style.strokeStyle) {
            return false;
        }
        ctx.beginPath();
        const width = this.applyScale(this._width, "");
        const height = this.applyScale(this._height, "");
        const offsetX = this.anchor === AnchorEnum.TopLeft ? 0 : Math.round(width / 2);
        const offsetY = this.anchor === AnchorEnum.TopLeft ? 0 : Math.round(height / 2);
        ctx.rect(this.applyTransformX(this._x, true) - offsetX, this.applyTransformY(this._y, true) - offsetY, width, height);
        ctx.closePath();
        this.drawShape(ctx, true, true);
        return true;
    }
    hit(p, additionalError) {
        this.hitExpansion = this.style.strokeStyle && this.style.lineWidth;
        return super.hit(p, additionalError);
    }
    set width(value) {
        if (value != this._width) {
            this.beforeUpdate();
            this._width = value;
            this.afterUpdate();
        }
    }
    set height(value) {
        if (value != this._height) {
            this.beforeUpdate();
            this._height = value;
            this.afterUpdate();
        }
    }
}
class LineElement extends ShapeElement {
    constructor(x1, y1, x2, y2, style, hitable, name) {
        super(style, hitable, name);
        this._x1 = x1;
        this._y1 = y1;
        this._x2 = x2;
        this._y2 = y2;
        this.anchor = AnchorEnum.TopLeft;
        this.setSize();
    }
    get x1() {
        return this._x1;
    }
    set x1(value) {
        if (value != this._x1) {
            this.setLine([value, this._y1, this._x2, this._y2]);
        }
    }
    get y1() {
        return this._y1;
    }
    set y1(value) {
        if (value != this._y1) {
            this.setLine([this._x1, value, this._x2, this._y2]);
        }
    }
    get x2() {
        return this._x2;
    }
    set x2(value) {
        if (value != this._x2) {
            this.setLine([this._x1, this._y1, value, this._y2]);
        }
    }
    get y2() {
        return this._y2;
    }
    set y2(value) {
        if (value != this._y2) {
            this.setLine([this._x1, this._y1, this._x2, value]);
        }
    }
    getLine() {
        return [this._x1, this._y1, this._x2, this._y2];
    }
    setLine(points) {
        if (points.length != 4) {
            throw new Error("4 points are needed to adjust the line");
        }
        this.invalidate();
        this._x1 = points[0];
        this._y1 = points[1];
        this._x2 = points[2];
        this._y2 = points[3];
        this.setSize();
        this.invalidate();
    }
    afterUpdate() {
        this.setSize();
        super.afterUpdate();
    }
    setSize() {
        var _a, _b;
        const lineAddition = (_b = this.style.getLineWidth((_a = this.board) === null || _a === void 0 ? void 0 : _a.transform)) !== null && _b !== void 0 ? _b : 1;
        const minX = Math.min(this.x1, this.x2);
        this._x = Math.floor(minX - lineAddition);
        const minY = Math.min(this.y1, this.y2);
        this._y = Math.floor(minY - lineAddition);
        this._width = Math.ceil(Math.max(this.x1, this.x2) - minX + lineAddition * 2);
        this._height = Math.ceil(Math.max(this.y1, this.y2) - minY + lineAddition * 2);
    }
    doDraw(ctx /*, clipRect?: Rect*/) {
        if (!this.style.strokeStyle) {
            return false;
        }
        ctx.beginPath();
        ctx.moveTo(this.applyTransformX(this.x1, true), this.applyTransformY(this.y1, true));
        ctx.lineTo(this.applyTransformX(this.x2, true), this.applyTransformY(this.y2, true));
        this.drawShape(ctx, true, false);
        return true;
    }
}
var PolygonHitType;
(function (PolygonHitType) {
    PolygonHitType[PolygonHitType["Dot"] = 0] = "Dot";
    PolygonHitType[PolygonHitType["Line"] = 1] = "Line";
    PolygonHitType[PolygonHitType["None"] = 2] = "None";
})(PolygonHitType || (PolygonHitType = {}));
class PolygonElement extends ShapeElement {
    constructor(points, style, dotStyle, dotRadius, closed, hitable, name) {
        super(style, hitable, name);
        this.dotStyle = dotStyle;
        this.dotRadius = dotRadius;
        this.closed = closed;
        this.anchor = AnchorEnum.TopLeft;
        this.points = points.slice();
        if (this.points.length) {
            this.setSize();
        }
    }
    afterUpdate() {
        this.setSize();
        super.afterUpdate();
    }
    setSize() {
        var _a, _b, _c, _d, _e, _f;
        const lineAddition = ((_b = this.style.getLineWidth((_a = this.board) === null || _a === void 0 ? void 0 : _a.transform)) !== null && _b !== void 0 ? _b : 1) + ((_e = (_c = this.dotStyle) === null || _c === void 0 ? void 0 : _c.getLineWidth((_d = this.board) === null || _d === void 0 ? void 0 : _d.transform)) !== null && _e !== void 0 ? _e : 0) + ((_f = this.dotRadius) !== null && _f !== void 0 ? _f : 0);
        if (!this.points.length) {
            this._x = this._y = this._width = this._height = 0;
            return;
        }
        const r = new Rect(this.points[0].x, this.points[0].y, 0, 0);
        for (let i = 1; i < this.points.length; i++) {
            r.addPoint(this.points[i]);
        }
        this._x = Math.floor(r.x - lineAddition);
        this._y = Math.floor(r.y - lineAddition);
        this._width = Math.ceil(r.width + lineAddition * 2);
        this._height = Math.ceil(r.height + lineAddition * 2);
    }
    doDraw(ctx /*, clipRect?: Rect*/) {
        if ((!this.style.strokeStyle && !this.style.fillStyle) || !this.points.length) {
            return false;
        }
        ctx.beginPath();
        ctx.moveTo(this.applyTransformX(this.points[0].x, true), this.applyTransformY(this.points[0].y, true));
        for (let i = 1; i < this.points.length; i++) {
            ctx.lineTo(this.applyTransformX(this.points[i].x, true), this.applyTransformY(this.points[i].y, true));
        }
        if (this.closed) {
            ctx.closePath();
        }
        this.drawShape(ctx, true, this.closed);
        if (this.dotRadius > 0 && this.dotStyle) {
            this.dotStyle.prepareCanvasContext(ctx, this);
            for (let i = 0; i < this.points.length; i++) {
                ctx.beginPath();
                ctx.arc(this.applyTransformX(this.points[i].x, true), this.applyTransformY(this.points[i].y, true), this.applyScale(this.dotRadius, "round"), 0, Math.PI * 2);
                this.drawShape(ctx, false, true, this.dotStyle);
            }
        }
        return true;
    }
    clear() {
        this.invalidate();
        this.points = [];
    }
    get length() {
        return this.points.length;
    }
    getPoints() {
        return this.points.map((p) => p.clone());
    }
    getPoint(index) {
        return this.points[index];
    }
    setPoint(index, newPosition) {
        this.invalidate();
        this.points[index].x = newPosition.x;
        this.points[index].y = newPosition.y;
        this.setSize();
        this.invalidate();
    }
    setPoints(points) {
        this.invalidate();
        for (const p of points) {
            this.points[p.index].x = p.newPosition.x;
            this.points[p.index].y = p.newPosition.y;
        }
        this.setSize();
        this.invalidate();
    }
    addPoint(p, index = -1) {
        if (index < 0 || index >= this.points.length) {
            this.points.push(new Point(p.x, p.y));
        }
        else {
            // Insert the point in the given position
            this.points.splice(index, -1, new Point(p.x, p.y));
        }
        this.setSize();
        this.invalidate();
    }
    removePoint(index) {
        if (index < 0 || index >= this.points.length) {
            throw new Error("Point index out of bounds");
        }
        this.invalidate();
        this.points.splice(index, 1);
        this.setSize();
        this.invalidate();
    }
    getHitComponent(p, additionalError) {
        var _a;
        // First check dots
        // As we have many points. It can happen that the hit hits more than one.
        // In this case we get the smallest distance
        let dotHit = null;
        for (let i = 0; i < this.points.length; i++) {
            const point = this.points[i];
            const distance = p.distance(point);
            if (distance < this.dotRadius + this.hitExpansion + additionalError && (!dotHit || distance < dotHit.distance)) {
                dotHit = { hitType: PolygonHitType.Dot, index: i, distance: distance };
            }
        }
        if (dotHit) {
            return dotHit;
        }
        // Check the lines
        for (let i = 0; i < this.points.length; i++) {
            const point = this.points[i];
            const next = this.points[(i + 1) % this.points.length];
            const distance = Segment.getDistanceToSegment(p, new Segment(point.x, point.y, next.x, next.y));
            if (distance < this.hitExpansion + this.style.lineWidth + ((_a = this.style.lineShadowSize) !== null && _a !== void 0 ? _a : 0) + additionalError) {
                return { hitType: PolygonHitType.Line, index: i, distance: distance };
            }
        }
        // TO DO: Check if inside of the polygon too?
        return { hitType: PolygonHitType.None };
    }
    simplify(tolerance, highestQuality) {
        this.beforeUpdate();
        this.points = simplify(this.points, tolerance, highestQuality);
        this.afterUpdate();
    }
    getStyle() {
        return this.style;
    }
    animatePoints(newPoints, durationMs, animationFunction) {
        if (newPoints.length != this.points.length) {
            throw new Error("Points length must be identical");
        }
        return this.animate("points", durationMs, newPoints, animationFunction, ResetAnimationEnum.Property);
    }
}

export { CircleElement, LineElement, PolygonElement, PolygonHitType, RectElement, ShapeElement };
//# sourceMappingURL=shape-element.js.map
